import React, { useState, useEffect, useMemo } from 'react';
import { Search, Sparkles, Copy, Check, Loader2, AlertCircle, ChevronDown, ChevronUp, Send } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

function App() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(''); // 'transcript' or 'summary'
  const [transcript, setTranscript] = useState('');
  const [summary, setSummary] = useState('');
  const [summaryType, setSummaryType] = useState('concise');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [videoId, setVideoId] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [transcriptVisible, setTranscriptVisible] = useState(false);

  // Fun loading messages to keep users entertained
  const transcriptMessages = useMemo(() => [
    "🎬 Analyzing video content like a detective on a mission...",
    "🔍 Deep-diving into the transcript ocean...",
    "🎯 Extracting wisdom from pixels and sound waves...",
    "💡 Converting video magic into written gold...",
    "🎪 Performing the transcript-to-summary circus act...",
    "🔬 Conducting scientific analysis of video content...",
    "🎨 Painting a masterpiece of knowledge...",
    "🚁 Hovering over the content to spot key insights...",
    "💎 Mining for intellectual gold...",
    "🎪 Setting up the knowledge circus...",
    "🔮 Predicting what you'll learn next...",
    "🌟 Making your brain cells dance with joy..."
  ], []);

  const summaryMessages = useMemo(() => [
    "🧠 Teaching AI to understand human speech (it's harder than it looks!)",
    "⚡ Processing faster than a student cramming for finals...",
    "🚀 Launching the summary rocket to knowledge orbit...",
    "🎓 Making complex topics as simple as ABC...",
    "📚 Building your personal knowledge library...",
    "🌟 Creating summaries that sparkle with insight...",
    "⚙️ Turning the gears of AI intelligence...",
    "🎭 Dramatizing the art of summarization...",
    "🎪 Juggling words and concepts like a pro...",
    "🔋 Charging up the knowledge batteries...",
    "🎯 Taking aim at the perfect summary...",
    "🌟 Making learning as fun as a roller coaster ride...",
    "🎪 Preparing the greatest show on Earth (of knowledge)...",
    "🚀 Counting down to knowledge liftoff...",
    "🎬 Rolling the credits on boredom...",
    "🎭 Rehearsing the perfect summary...",
    "🎪 Preparing the knowledge feast...",
    "🚁 Landing the helicopter of wisdom...",
    "🎯 Bullseye! Almost there...",
    "🌟 Sprinkling knowledge fairy dust...",
    "🎪 Final act of the learning show...",
    "🚀 Preparing for knowledge touchdown..."
  ], []);

  // Rotate loading messages while processing
  useEffect(() => {
    let interval;
    if (loading) {
      let messageIndex = 0;
      const currentMessages = loadingPhase === 'transcript' ? transcriptMessages : summaryMessages;
      setLoadingMessage(currentMessages[0]);
      
      interval = setInterval(() => {
        messageIndex = (messageIndex + 1) % currentMessages.length;
        setLoadingMessage(currentMessages[messageIndex]);
      }, 2000); // Change message every 2 seconds
    } else {
      setLoadingMessage('');
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading, loadingPhase, transcriptMessages, summaryMessages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;

    setLoading(true);
    setError('');
    setTranscript('');
    setSummary('');
    setVideoId('');

    try {
      // Extract transcript
      setLoadingPhase('transcript');
      const transcriptResponse = await axios.post('/api/extract-transcript', { url });
      const { transcript: extractedTranscript, videoId: extractedVideoId } = transcriptResponse.data;
      
      setTranscript(extractedTranscript);
      setVideoId(extractedVideoId);

      // Generate summary
      setLoadingPhase('summary');
      const summaryResponse = await axios.post('/api/summarize', {
        transcript: extractedTranscript,
        summaryType
      });
      
      setSummary(summaryResponse.data.summary);
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const getSummaryTypeLabel = (type) => {
    switch (type) {
      case 'concise': return '🎯 Executive Summary';
      case 'detailed': return '📚 Study Guide';
      case 'bullet': return '⚡ Action Plan';
      case 'academic': return '🔬 Academic Paper';
      case 'social': return '📱 Social Media';
      default: return '🎯 Executive Summary';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(120,119,198,0.1)_0%,transparent_50%)]"></div>
      
             <div className="container mx-auto px-6 py-20 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
                     <h1 className="text-5xl font-bold text-white mb-4 font-futuristic">
             <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">YouTube Summarizer</span>
           </h1>
          <div className="flex justify-center items-center gap-2 mb-8">
            <div className="w-2 h-2 bg-white rounded-full"></div>
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
                     <p className="text-lg text-white/60 max-w-2xl mx-auto font-modern">
             Transform any YouTube video into a powerful learning resource. Perfect for students, researchers, content creators, and professionals.
           </p>
        </div>

        {/* Main Search Interface */}
        <div className="max-w-4xl mx-auto mb-16">
          <form onSubmit={handleSubmit} className="relative">
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                <Search className="w-6 h-6 text-white/70" />
              </div>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Paste a YouTube URL to get started..."
                className="w-full bg-black/20 backdrop-blur-xl border border-purple-500/30 rounded-2xl py-6 pl-16 pr-20 text-white text-lg placeholder-white/50 focus:outline-none focus:border-purple-400/60 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                required
              />
              <button
                type="submit"
                disabled={loading || !url.trim()}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 disabled:opacity-50 disabled:cursor-not-allowed p-3 rounded-xl transition-all duration-300 hover:scale-105"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Send className="w-5 h-5 text-white" />
                )}
              </button>
            </div>
            
            {/* Summary Type Selector */}
            <div className="mt-6 flex justify-center">
              <div className="bg-black/20 backdrop-blur-xl border border-purple-500/20 rounded-xl p-2">
                                 <select
                   value={summaryType}
                   onChange={(e) => setSummaryType(e.target.value)}
                   className="bg-transparent text-white border-none outline-none px-4 py-2 cursor-pointer font-modern [&>option]:text-black [&>option]:bg-white"
                 >
                  <option value="concise">🎯 Executive Summary</option>
                  <option value="detailed">📚 Study Guide</option>
                  <option value="bullet">⚡ Action Plan</option>
                  <option value="academic">🔬 Academic Paper</option>
                  <option value="social">📱 Social Media</option>
                </select>
              </div>
            </div>
          </form>
        </div>

        {/* Loading Message Display */}
        {loading && loadingMessage && (
          <div className="max-w-2xl mx-auto mb-12 text-center">
            <div className="bg-black/20 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-8">
              <div className="mb-4">
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-purple-500/20 text-purple-300 border border-purple-400/30">
                  {loadingPhase === 'transcript' ? '🎬 Extracting Transcript' : '🧠 Generating Summary'}
                </span>
              </div>
              <p className="text-white/80 text-lg font-modern">
                {loadingMessage}
              </p>
              <div className="mt-4 w-16 h-0.5 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mx-auto animate-pulse"></div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="bg-red-500/10 backdrop-blur-xl border border-red-500/30 rounded-2xl p-6">
              <div className="flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-400 mr-3" />
                <span className="text-red-200 text-lg font-modern">{error}</span>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {transcript && (
          <div className="space-y-8 max-w-4xl mx-auto">
            {/* Summary */}
            <div className="bg-black/20 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white flex items-center font-tech">
                  <Sparkles className="w-6 h-6 mr-3 text-purple-400" />
                  {getSummaryTypeLabel(summaryType)}
                </h3>
                <button
                  onClick={() => copyToClipboard(summary)}
                  className="bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 hover:border-purple-400/50 p-3 rounded-xl transition-all duration-300"
                  title="Copy summary"
                >
                  {copied ? (
                    <Check className="w-5 h-5 text-green-400" />
                  ) : (
                    <Copy className="w-5 h-5 text-purple-300" />
                  )}
                </button>
              </div>
              <div className="bg-black/10 rounded-xl p-6 border border-purple-500/20">
                <div className="prose prose-lg max-w-none prose-invert">
                  <ReactMarkdown>{summary}</ReactMarkdown>
                </div>
              </div>
            </div>

            {/* Collapsible Transcript */}
            <div className="bg-black/20 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6">
              <button
                onClick={() => setTranscriptVisible(!transcriptVisible)}
                className="w-full flex items-center justify-between mb-4 p-4 hover:bg-white/5 rounded-xl transition-all duration-300"
              >
                <h3 className="text-xl font-semibold text-white flex items-center font-tech">
                  <Sparkles className="w-5 h-5 mr-3 text-purple-400" />
                  Transcript
                </h3>
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(transcript);
                    }}
                    className="bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 hover:border-purple-400/50 p-2 rounded-lg transition-all duration-300"
                    title="Copy transcript"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-purple-300" />
                    )}
                  </button>
                  {transcriptVisible ? (
                    <ChevronUp className="w-5 h-5 text-white/70" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white/70" />
                  )}
                </div>
              </button>
              
              {transcriptVisible && (
                <div className="bg-black/10 rounded-xl p-6 border-t border-purple-500/20 max-h-96 overflow-y-auto">
                  <p className="text-white/80 text-base leading-relaxed whitespace-pre-wrap font-modern">{transcript}</p>
                </div>
              )}
            </div>

            {/* Video Preview */}
            {videoId && (
              <div className="bg-black/20 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center font-tech">
                  <Sparkles className="w-5 h-5 mr-3 text-purple-400" />
                  Video Preview
                </h3>
                <div className="bg-black/10 rounded-xl p-4 border border-purple-500/20">
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}`}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full rounded-lg"
                    ></iframe>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


      </div>
    </div>
  );
}

export default App;
